import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { Controller, FieldValues, useFormContext } from "react-hook-form";

import { FieldLabel } from "@/components/Form/Base/fieldLabel.component";
import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Input } from "@/components/Form/Base/input.component";
import { InputGroup } from "@/components/Form/Base/inputGroup.component";
import { InputGroupText } from "@/components/Form/Base/inputGroupText.component";
import { FormFeedback } from "@/components/Form/RHF/FormElements/formFeedback.component";
import { FormValuesExploded } from "@/components/Form/RHF/FormFields/formField.types";
import { useNumericField } from "@/components/Form/RHF/FormFields/NumericField/NumericField.hooks";
import { calculateFieldState } from "@/components/Form/RHF/Utils/form.helpers";

type YieldCalculatorFieldProps<T extends FieldValues> =
    FormValuesExploded<T> & {
        placeholder?: string;
        unit?: string;
        buttonLabel: string;
    };

const YieldCalculatorField = <T extends FieldValues>({
    name,
    label,
    required,
    placeholder,
    unit,
    buttonLabel
}: YieldCalculatorFieldProps<T>) => {
    const { control } = useFormContext<T>();
    const { onKeyDownHandler, onBlurHandler, onChangeHandler, onPasteHandler } =
        useNumericField<T>({
            name,
            required,
            defaultValue: 0
        });

    return (
        <FormGroup>
            <FieldLabel
                name={"surface"}
                label={label}
                required={true}
                requiredIndication={false}
            />
            <div className={cn("tw:flex")}>
                <Controller<T>
                    control={control}
                    name={"surface"}
                    render={({ field, fieldState, formState }) => {
                        const { isInvalid, isValid } = calculateFieldState(
                            fieldState,
                            formState
                        );

                        const handleOnChange = onChangeHandler(
                            field.value,
                            field.onChange
                        );

                        return (
                            <div className={cn("tw:flex tw:flex-col")}>
                                <InputGroup>
                                    <Input
                                        {...field}
                                        type="number"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        onChange={handleOnChange}
                                        onKeyDown={onKeyDownHandler}
                                        onBlur={onBlurHandler}
                                        onPaste={onPasteHandler}
                                        autoComplete="off"
                                        id={field.name}
                                        placeholder={placeholder}
                                        valid={isValid}
                                        invalid={isInvalid}
                                    />
                                    {!!unit && (
                                        <InputGroupText>{unit}</InputGroupText>
                                    )}
                                </InputGroup>
                                <FormFeedback
                                    name={field.name}
                                    label={label}
                                />
                            </div>
                        );
                    }}
                />
                <div
                    className={cn(
                        "tw:ml-3",
                        "tw:grow-0 tw:shrink-0 tw:basis-auto tw:w-auto tw:max-w-none"
                    )}
                >
                    <Button
                        type={"submit"}
                        className={cn("tw:py-1.75")}
                    >
                        {buttonLabel}
                    </Button>
                </div>
            </div>
        </FormGroup>
    );
};

export { YieldCalculatorField };
